import React, { useState } from 'react';
import { Spin, Drawer, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, PageLayout } from '../../ui';
import {
  useGetCarriersControlSummaryQuery,
  useGetCarrierRoutesWithIssuesQuery,
  type CarrierControlSummary,
  type CarrierRouteWithIssues
} from '../../store/api/financeApi';

const CarriersControlPage: React.FC = () => {
  const { data, isLoading } = useGetCarriersControlSummaryQuery({});
  const [drawerCarrierId, setDrawerCarrierId] = useState<number | null>(null);
  const [drawerCarrierName, setDrawerCarrierName] = useState('');

  const { data: routesData, isLoading: routesLoading } = useGetCarrierRoutesWithIssuesQuery(
    { carrierId: drawerCarrierId! },
    { skip: drawerCarrierId === null }
  );

  React.useEffect(() => {
    console.log('PHASE II.1 CARRIER CONTROL READY');
  }, []);

  const handleRowClick = (record: CarrierControlSummary) => {
    if (record.carrierId === null) return;
    setDrawerCarrierId(record.carrierId);
    setDrawerCarrierName(record.carrierName);
  };

  const columns: ColumnsType<CarrierControlSummary> = [
    { title: 'Перевозчик', dataIndex: 'carrierName', key: 'carrierName' },
    { title: 'Маршрутов', dataIndex: 'totalRoutes', key: 'totalRoutes', width: 100 },
    { title: 'С проблемами', dataIndex: 'routesWithIssues', key: 'routesWithIssues', width: 120 },
    { title: 'Критичные', dataIndex: 'criticalCount', key: 'criticalCount', width: 90 },
    { title: 'Опоздания (погрузка)', dataIndex: 'lateLoadingCount', key: 'lateLoadingCount', width: 140 },
    { title: 'Опоздания (выгрузка)', dataIndex: 'lateUnloadingCount', key: 'lateUnloadingCount', width: 150 },
    { title: 'Нет фото', dataIndex: 'missingPhotosCount', key: 'missingPhotosCount', width: 90 },
    { title: 'Просрочка оригиналов', dataIndex: 'originalsDelayCount', key: 'originalsDelayCount', width: 150 }
  ];

  const drawerColumns: ColumnsType<CarrierRouteWithIssues> = [
    { title: 'Маршрут', dataIndex: 'routeId', key: 'routeId', render: (v: number) => `#${v}` },
    { title: 'Тип', dataIndex: 'transportType', key: 'transportType', width: 100 },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Критичные',
      key: 'critical',
      width: 90,
      render: (_: any, r: CarrierRouteWithIssues) => {
        const n = r.controlIssues?.critical?.length ?? 0;
        return n > 0 ? <Tag color="red">{n}</Tag> : '—';
      }
    },
    {
      title: 'Предупреждения',
      key: 'warnings',
      width: 120,
      render: (_: any, r: CarrierRouteWithIssues) => {
        const n = r.controlIssues?.warnings?.length ?? 0;
        return n > 0 ? <Tag color="gold">{n}</Tag> : '—';
      }
    },
    {
      title: 'Описание проблем',
      dataIndex: 'issuesDescription',
      key: 'issuesDescription',
      ellipsis: true
    }
  ];

  if (isLoading) {
    return (
      <PageLayout title="Контроль перевозчиков" subtitle="Загрузка сводки…">
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Контроль перевозчиков" subtitle="Нарушения и качество исполнения по перевозчикам">
      <DataTable
        rowKey={(r) => String(r.carrierId ?? 'unassigned')}
        columns={columns}
        dataSource={data?.carriers ?? []}
        pagination={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: {
            cursor: record.carrierId !== null ? 'pointer' : 'default'
          }
        })}
      />

      <Drawer
        title={`Маршруты с нарушениями: ${drawerCarrierName}`}
        placement="right"
        width={720}
        onClose={() => setDrawerCarrierId(null)}
        open={drawerCarrierId !== null}
      >
        {routesLoading ? (
          <Spin />
        ) : !routesData?.routes?.length ? (
          <div>У перевозчика нет маршрутов с нарушениями</div>
        ) : (
          <DataTable
            rowKey="routeId"
            columns={drawerColumns}
            dataSource={routesData.routes}
            pagination={false}
            size="small"
          />
        )}
      </Drawer>
    </PageLayout>
  );
};

export default CarriersControlPage;
